import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  getMasterData, createMasterData, updateMasterData, deleteMasterData,
  importMasterData, extractMasterDataFromPdf, getPdfExtractionStatus, uploadMasterDataSheet,
  getPendingMasterData, approveMasterDataChange, rejectMasterDataChange,
} from '@/lib/api';
import { Plus, Search, Trash2, Edit, Upload, Database, Loader2, ChevronLeft, ChevronRight, FileText, Sparkles, Check, X, FileUp, ExternalLink, ShieldCheck, Clock } from 'lucide-react';
import * as XLSX from 'xlsx';

const EMPTY_FORM = {
  supplier_name: '', supplier_article_number: '', product_name: '', material_type: '',
  face_material: '', adhesive_type: '', liner_type: '', total_thickness_um: '',
  basis_weight_gm2: '', width_mm: '', application_temp_min: '', application_temp_max: '',
  certifications: '', fsc: false, food_safe: false, ean_code: '', spec_code: '', data_sheet_url: '', notes: '',
};

const STATUS_META = {
  approved:       { label: 'Freigegeben',      cls: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300', icon: ShieldCheck },
  pending_new:    { label: 'Neu – wartet',     cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',          icon: Clock },
  pending_update: { label: 'Änderung – wartet',cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',          icon: Clock },
  pending_delete: { label: 'Löschung – wartet',cls: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',                  icon: Clock },
};

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || STATUS_META.approved;
  const Icon = meta.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${meta.cls}`}
      data-testid={`master-status-${status}`}
    >
      <Icon className="h-3 w-3" />
      {meta.label}
    </span>
  );
}

export function MasterDataTab() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 1, pending_count: 0 });
  const [pendingQueue, setPendingQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [dialog, setDialog] = useState({ open: false, mode: 'create', item: null });
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, item: null });
  const [importing, setImporting] = useState(false);
  const [pdfExtracting, setPdfExtracting] = useState(false);
  const [pdfResults, setPdfResults] = useState(null);
  const fileRef = useRef(null);
  const pdfRef = useRef(null);
  const dataSheetRef = useRef(null);
  const [uploadingSheetFor, setUploadingSheetFor] = useState(null);

  useEffect(() => { loadData(); }, [page, search]);
  useEffect(() => { if (isAdmin) loadPendingQueue(); }, [isAdmin]);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await getMasterData({ search, page, limit: 20 });
      setData(res.data);
    } catch { toast.error(t('admin.md.loadError')); }
    finally { setLoading(false); }
  };

  const loadPendingQueue = async () => {
    try {
      const res = await getPendingMasterData();
      setPendingQueue(res.data?.items || []);
    } catch { /* silent — non-admins hit 403 here */ }
  };

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setDialog({ open: true, mode: 'create', item: null });
  };

  const openEdit = (item) => {
    setForm({
      supplier_name: item.supplier_name || '',
      supplier_article_number: item.supplier_article_number || '',
      product_name: item.product_name || '',
      material_type: item.material_type || '',
      face_material: item.face_material || '',
      adhesive_type: item.adhesive_type || '',
      liner_type: item.liner_type || '',
      total_thickness_um: item.total_thickness_um || '',
      basis_weight_gm2: item.basis_weight_gm2 || '',
      width_mm: item.width_mm || '',
      application_temp_min: item.application_temp_min || '',
      application_temp_max: item.application_temp_max || '',
      certifications: item.certifications || '',
      fsc: item.fsc || false,
      food_safe: item.food_safe || false,
      ean_code: item.ean_code || '',
      spec_code: item.spec_code || '',
      data_sheet_url: item.data_sheet_url || '',
      notes: item.notes || '',
    });
    setDialog({ open: true, mode: 'edit', item });
  };

  const handleSave = async () => {
    if (!form.supplier_name.trim() || !form.supplier_article_number.trim()) {
      toast.error(t('admin.md.supplierRequired'));
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        total_thickness_um: form.total_thickness_um ? parseFloat(form.total_thickness_um) : null,
        basis_weight_gm2: form.basis_weight_gm2 ? parseFloat(form.basis_weight_gm2) : null,
        width_mm: form.width_mm ? parseFloat(form.width_mm) : null,
        application_temp_min: form.application_temp_min ? parseFloat(form.application_temp_min) : null,
        application_temp_max: form.application_temp_max ? parseFloat(form.application_temp_max) : null,
      };
      if (dialog.mode === 'edit') {
        const res = await updateMasterData(dialog.item.master_id, payload);
        const newStatus = res?.data?.approval_status;
        if (!isAdmin && (newStatus === 'pending_update' || newStatus === 'pending_new')) {
          toast.success(t('admin.md.changeSubmitted'));
        } else {
          toast.success('Stammdaten aktualisiert');
        }
      } else {
        await createMasterData(payload);
        toast.success(isAdmin
          ? 'Stammdaten erstellt'
          : 'Eintrag eingereicht – wartet auf Admin-Freigabe');
      }
      setDialog({ open: false, mode: 'create', item: null });
      loadData();
      if (isAdmin) loadPendingQueue();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('admin.common.saveError'));
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      const res = await deleteMasterData(deleteDialog.item.master_id);
      const newStatus = res?.data?.approval_status;
      if (!isAdmin && newStatus === 'pending_delete') {
        toast.success(t('admin.md.deleteRequested'));
      } else {
        toast.success(t('admin.md.masterDataDeleted'));
      }
      setDeleteDialog({ open: false, item: null });
      loadData();
      if (isAdmin) loadPendingQueue();
    } catch { toast.error(t('admin.md.deleteError')); }
  };

  const handleApprove = async (item) => {
    try {
      await approveMasterDataChange(item.master_id);
      toast.success(t('admin.md.changeApproved'));
      loadData();
      loadPendingQueue();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler bei der Freigabe');
    }
  };

  const handleReject = async (item) => {
    const notes = typeof window !== 'undefined'
      ? window.prompt('Optionaler Ablehnungsgrund (wird im Audit-Log gespeichert)')
      : null;
    if (notes === null) return; // cancelled
    try {
      await rejectMasterDataChange(item.master_id, notes || '');
      toast.success(t('admin.md.changeRejected'));
      loadData();
      loadPendingQueue();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Ablehnen');
    }
  };

  const handleFileImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const arrayBuffer = await file.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet);
      if (rows.length === 0) { toast.error(t('admin.md.noDataFound')); return; }
      const res = await importMasterData(rows);
      const { imported, updated, pending, errors } = res.data;
      const summary = isAdmin
        ? `Import: ${imported} neu, ${updated} aktualisiert${errors?.length ? `, ${errors.length} Fehler` : ''}`
        : `Import: ${pending || 0} eingereicht (wartet auf Admin-Freigabe)${errors?.length ? `, ${errors.length} Fehler` : ''}`;
      toast.success(summary);
      loadData();
      if (isAdmin) loadPendingQueue();
    } catch (err) {
      toast.error('Import fehlgeschlagen: ' + (err.message || ''));
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handlePdfExtract = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPdfExtracting(true);
    setPdfResults(null);
    try {
      // Step 1: Upload and start job
      const startRes = await extractMasterDataFromPdf(file);
      const jobId = startRes.data.job_id;
      if (!jobId) throw new Error('Kein Job-ID erhalten');

      // Step 2: Poll for result
      let attempts = 0;
      const maxAttempts = 60; // 60 * 3s = 180s max
      while (attempts < maxAttempts) {
        await new Promise(r => setTimeout(r, 3000));
        const pollRes = await getPdfExtractionStatus(jobId);
        const job = pollRes.data;

        if (job.status === 'done') {
          setPdfResults(job);
          toast.success(`KI hat ${job.extracted} Artikel aus ${job.pages_processed} Seite(n) extrahiert`);
          loadData();
          return;
        }
        if (job.status === 'error') {
          toast.error('KI-Extraktion fehlgeschlagen: ' + (job.error || 'Unbekannter Fehler'));
          return;
        }
        attempts++;
      }
      toast.error(t('admin.md.aiTimeout'));
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Unbekannter Fehler';
      toast.error('PDF-Extraktion fehlgeschlagen: ' + msg);
    } finally {
      setPdfExtracting(false);
      if (pdfRef.current) pdfRef.current.value = '';
    }
  };

  const handleDataSheetUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !uploadingSheetFor) return;
    try {
      await uploadMasterDataSheet(uploadingSheetFor, file);
      toast.success('Datenblatt hochgeladen');
      loadData();
    } catch (err) {
      toast.error(`${t('admin.md.uploadError')}: ` + (err.response?.data?.detail || err.message));
    } finally {
      setUploadingSheetFor(null);
      if (dataSheetRef.current) dataSheetRef.current.value = '';
    }
  };

  const downloadTemplate = () => {
    const headers = Object.keys(EMPTY_FORM);
    const ws = XLSX.utils.aoa_to_sheet([headers, ['Avery Dennison', 'AD225', 'TRANSFER PET WHITE PT', 'Label Material', 'Polyester', 'S8030 Acrylate', 'BG42 Glassine', '130', '71', '', '-40', '150', 'FSC, UL 969', '8712739306460', 'AD225', '', '']]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Master Data');
    XLSX.writeFile(wb, 'stammdaten_vorlage.xlsx');
  };

  return (
    <div className="space-y-4" data-testid="master-data-tab">
      {/* Non-admin banner: make the approval flow obvious */}
      {!isAdmin && (
        <Card className="border-amber-200 bg-amber-50/50 dark:bg-amber-900/10" data-testid="master-data-approval-banner">
          <CardContent className="py-3 px-4 text-sm text-amber-900 dark:text-amber-200 flex items-start gap-2">
            <ShieldCheck className="h-4 w-4 mt-0.5 shrink-0 text-amber-600" />
            <div>
              <p className="font-semibold">Freigabepflichtig</p>
              <p className="text-xs mt-0.5">
                Als Gruppen- oder Werksadmin können Sie Stammdaten anlegen und ändern. Jede Aktion wird
                als Entwurf gespeichert und ist erst nach Freigabe durch den Hauptadmin im Matching wirksam.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Admin approval queue */}
      {isAdmin && pendingQueue.length > 0 && (
        <Card className="border-amber-200 bg-amber-50/30" data-testid="master-data-pending-queue">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Clock className="h-4 w-4 text-amber-600" />
              {pendingQueue.length} offene Stammdaten-Änderung{pendingQueue.length === 1 ? '' : 'en'}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {pendingQueue.map((item) => (
                <div
                  key={item.master_id}
                  className="flex flex-wrap items-center gap-3 p-2 bg-white dark:bg-slate-900 rounded border text-xs"
                  data-testid={`master-pending-${item.master_id}`}
                >
                  <StatusBadge status={item.approval_status} />
                  <div className="flex-1 min-w-[200px]">
                    <span className="font-medium">{item.supplier_name}</span>
                    <span className="text-muted-foreground mx-1">|</span>
                    <span>{item.supplier_article_number}</span>
                    {item.product_name && <span className="text-muted-foreground ml-2">– {item.product_name}</span>}
                  </div>
                  <span className="text-muted-foreground text-[11px]">
                    {item.submitter?.name || item.submitter?.email || 'unbekannt'}
                    {item.submitter?.role ? ` · ${item.submitter.role}` : ''}
                  </span>
                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                      onClick={() => handleApprove(item)}
                      data-testid={`master-approve-${item.master_id}`}
                    >
                      <Check className="h-3.5 w-3.5 mr-1" />
                      Freigeben
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs border-red-300 text-red-600 hover:bg-red-50"
                      onClick={() => handleReject(item)}
                      data-testid={`master-reject-${item.master_id}`}
                    >
                      <X className="h-3.5 w-3.5 mr-1" />
                      Ablehnen
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Header with actions */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t('masterData.searchPlaceholder') || 'Suche nach Lieferant, Artikelnr...'}
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="pl-10"
            data-testid="master-data-search"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="master-data-template-btn">
            <Database className="h-4 w-4 mr-1" />
            Vorlage
          </Button>
          <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={importing} data-testid="master-data-import-btn">
            {importing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}
            Excel Import
          </Button>
          <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={handleFileImport} />
          <Button
            size="sm"
            variant="outline"
            onClick={() => pdfRef.current?.click()}
            disabled={pdfExtracting}
            className="border-violet-300 text-violet-700 hover:bg-violet-50"
            data-testid="master-data-pdf-extract-btn"
          >
            {pdfExtracting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <><Sparkles className="h-4 w-4 mr-1" /><FileText className="h-4 w-4 mr-1" /></>}
            {pdfExtracting ? 'KI analysiert...' : 'PDF → KI Import'}
          </Button>
          <input ref={pdfRef} type="file" accept=".pdf" className="hidden" onChange={handlePdfExtract} />
          <input ref={dataSheetRef} type="file" accept=".pdf" className="hidden" onChange={handleDataSheetUpload} />
          <Button size="sm" onClick={openCreate} className="bg-orange-500 hover:bg-orange-600" data-testid="master-data-create-btn">
            <Plus className="h-4 w-4 mr-1" />
            Neu
          </Button>
        </div>
      </div>

      {/* PDF AI Extraction Results */}
      {pdfResults && (
        <Card className="border-violet-200 bg-violet-50/30">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-violet-600" />
                KI-Extraktion: {pdfResults.saved} Artikel aus {pdfResults.pages_processed} Seite(n)
              </CardTitle>
              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setPdfResults(null)} data-testid="pdf-results-close">
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {pdfResults.items.map((item, idx) => (
                <div key={idx} className="flex items-center gap-3 p-2 bg-white rounded border text-xs" data-testid={`pdf-result-${idx}`}>
                  <div className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-white ${item._action === 'created' ? 'bg-green-500' : 'bg-blue-500'}`}>
                    <Check className="h-3 w-3" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="font-medium">{item.supplier_name}</span>
                    <span className="text-muted-foreground mx-1">|</span>
                    <span>{item.supplier_article_number}</span>
                    {item.product_name && <span className="text-muted-foreground ml-2">– {item.product_name}</span>}
                  </div>
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${item._action === 'created' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                    {item._action === 'created' ? 'Neu' : 'Aktualisiert'}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* PDF Extraction Loading Overlay */}
      {pdfExtracting && (
        <Card className="border-violet-200 bg-violet-50/50">
          <CardContent className="flex items-center justify-center gap-3 py-8">
            <Loader2 className="h-6 w-6 animate-spin text-violet-600" />
            <div className="text-sm">
              <p className="font-medium text-violet-800">KI analysiert PDF...</p>
              <p className="text-violet-600 text-xs">Kimi K2.5 extrahiert Materialdaten. Dies kann 30-60 Sekunden dauern.</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="master-data-table">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="text-left p-3 font-medium">Lieferant</th>
                  <th className="text-left p-3 font-medium">Artikelnr.</th>
                  <th className="text-left p-3 font-medium hidden md:table-cell">Produktname</th>
                  <th className="text-left p-3 font-medium hidden md:table-cell">Status</th>
                  <th className="text-left p-3 font-medium hidden lg:table-cell">Dicke (μm)</th>
                  <th className="text-center p-3 font-medium hidden lg:table-cell">FSC</th>
                  <th className="text-center p-3 font-medium hidden lg:table-cell">Food Safe</th>
                  <th className="text-center p-3 font-medium hidden lg:table-cell">Datenblatt</th>
                  <th className="text-right p-3 font-medium">Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={9} className="p-8 text-center text-muted-foreground">Laden...</td></tr>
                ) : data.items.length === 0 ? (
                  <tr><td colSpan={9} className="p-8 text-center text-muted-foreground">
                    {search ? 'Keine Ergebnisse' : 'Noch keine Stammdaten. Erstellen Sie einen Eintrag oder importieren Sie eine Excel-Datei.'}
                  </td></tr>
                ) : data.items.map((item) => (
                  <tr key={item.master_id} className="border-b hover:bg-muted/30 transition-colors" data-testid={`master-row-${item.master_id}`}>
                    <td className="p-3 font-medium">{item.supplier_name}</td>
                    <td className="p-3">{item.supplier_article_number}</td>
                    <td className="p-3 hidden md:table-cell text-muted-foreground truncate max-w-[200px]">{item.product_name || '-'}</td>
                    <td className="p-3 hidden md:table-cell">
                      <StatusBadge status={item.approval_status || 'approved'} />
                    </td>
                    <td className="p-3 hidden lg:table-cell">{item.total_thickness_um || '-'}</td>
                    <td className="p-3 hidden lg:table-cell text-center">
                      {item.fsc ? <Badge className="bg-green-600 text-white text-xs">FSC</Badge> : <span className="text-muted-foreground text-xs">-</span>}
                    </td>
                    <td className="p-3 hidden lg:table-cell text-center">
                      {item.food_safe ? <Badge className="bg-blue-600 text-white text-xs">Food Safe</Badge> : <span className="text-muted-foreground text-xs">-</span>}
                    </td>
                    <td className="p-3 hidden lg:table-cell text-center">
                      {item.data_sheet_url ? (
                        <a href={item.data_sheet_url} target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-violet-700 hover:text-violet-900 bg-violet-50 px-2 py-1 rounded"
                          data-testid={`master-sheet-link-${item.master_id}`}>
                          <FileText className="h-3.5 w-3.5" />
                          PDF
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      ) : (
                        <Button variant="ghost" size="sm" className="h-7 text-xs text-muted-foreground"
                          onClick={() => { setUploadingSheetFor(item.master_id); dataSheetRef.current?.click(); }}
                          data-testid={`master-sheet-upload-${item.master_id}`}>
                          <FileUp className="h-3.5 w-3.5 mr-1" />
                          PDF
                        </Button>
                      )}
                    </td>
                    <td className="p-3 text-right">
                      <div className="flex gap-1 justify-end">
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(item)} data-testid={`master-edit-${item.master_id}`}>
                          <Edit className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:text-red-600" onClick={() => setDeleteDialog({ open: true, item })} data-testid={`master-delete-${item.master_id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Pagination */}
          {data.pages > 1 && (
            <div className="flex items-center justify-between p-3 border-t">
              <span className="text-xs text-muted-foreground">{data.total} Einträge</span>
              <div className="flex gap-1">
                <Button variant="ghost" size="icon" className="h-7 w-7" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm px-2 py-1">{page} / {data.pages}</span>
                <Button variant="ghost" size="icon" className="h-7 w-7" disabled={page >= data.pages} onClick={() => setPage(p => p + 1)}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialog.open} onOpenChange={(open) => !open && setDialog({ open: false, mode: 'create', item: null })}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{dialog.mode === 'edit' ? 'Stammdaten bearbeiten' : 'Neue Stammdaten'}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 py-2">
            <div>
              <Label>Lieferant *</Label>
              <Input value={form.supplier_name} onChange={(e) => setForm(p => ({ ...p, supplier_name: e.target.value }))}
                placeholder="z.B. Avery Dennison" data-testid="master-form-supplier" />
            </div>
            <div>
              <Label>Lieferanten-Artikelnr. *</Label>
              <Input value={form.supplier_article_number} onChange={(e) => setForm(p => ({ ...p, supplier_article_number: e.target.value }))}
                placeholder="z.B. AD225" data-testid="master-form-article" />
            </div>
            <div className="md:col-span-2">
              <Label>Produktname</Label>
              <Input value={form.product_name} onChange={(e) => setForm(p => ({ ...p, product_name: e.target.value }))}
                placeholder="z.B. TRANSFER PET WHITE PT" data-testid="master-form-product" />
            </div>
            <div>
              <Label>Materialtyp</Label>
              <Input value={form.material_type} onChange={(e) => setForm(p => ({ ...p, material_type: e.target.value }))}
                placeholder="z.B. Label Material" />
            </div>
            <div>
              <Label>Obermaterial</Label>
              <Input value={form.face_material} onChange={(e) => setForm(p => ({ ...p, face_material: e.target.value }))}
                placeholder="z.B. Polyester" />
            </div>
            <div>
              <Label>Klebstofftyp</Label>
              <Input value={form.adhesive_type} onChange={(e) => setForm(p => ({ ...p, adhesive_type: e.target.value }))}
                placeholder="z.B. S8030 Acrylate" />
            </div>
            <div>
              <Label>{t('admin.md.backingMaterial')}</Label>
              <Input value={form.liner_type} onChange={(e) => setForm(p => ({ ...p, liner_type: e.target.value }))}
                placeholder="z.B. BG42 Glassine" />
            </div>
            <div>
              <Label>Gesamtdicke (μm)</Label>
              <Input type="number" value={form.total_thickness_um} onChange={(e) => setForm(p => ({ ...p, total_thickness_um: e.target.value }))}
                placeholder="130" />
            </div>
            <div>
              <Label>{t('admin.md.areaWeight')}</Label>
              <Input type="number" value={form.basis_weight_gm2} onChange={(e) => setForm(p => ({ ...p, basis_weight_gm2: e.target.value }))}
                placeholder="71" />
            </div>
            <div>
              <Label>Breite (mm)</Label>
              <Input type="number" value={form.width_mm} onChange={(e) => setForm(p => ({ ...p, width_mm: e.target.value }))} />
            </div>
            <div>
              <Label>Min. Temp. (°C)</Label>
              <Input type="number" value={form.application_temp_min} onChange={(e) => setForm(p => ({ ...p, application_temp_min: e.target.value }))}
                placeholder="-40" />
            </div>
            <div>
              <Label>Max. Temp. (°C)</Label>
              <Input type="number" value={form.application_temp_max} onChange={(e) => setForm(p => ({ ...p, application_temp_max: e.target.value }))}
                placeholder="150" />
            </div>
            <div className="md:col-span-2">
              <Label>Zertifizierungen</Label>
              <Input value={form.certifications} onChange={(e) => setForm(p => ({ ...p, certifications: e.target.value }))}
                placeholder="z.B. FSC, UL 969" />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox 
                id="master-fsc" 
                checked={form.fsc} 
                onCheckedChange={(checked) => setForm(p => ({ ...p, fsc: !!checked }))} 
                data-testid="master-form-fsc"
              />
              <Label htmlFor="master-fsc" className="cursor-pointer">FSC-zertifiziert</Label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox 
                id="master-food-safe" 
                checked={form.food_safe} 
                onCheckedChange={(checked) => setForm(p => ({ ...p, food_safe: !!checked }))} 
                data-testid="master-form-food-safe"
              />
              <Label htmlFor="master-food-safe" className="cursor-pointer">Food Safe</Label>
            </div>
            <div>
              <Label>EAN-Code</Label>
              <Input value={form.ean_code} onChange={(e) => setForm(p => ({ ...p, ean_code: e.target.value }))} />
            </div>
            <div>
              <Label>Spec-Code</Label>
              <Input value={form.spec_code} onChange={(e) => setForm(p => ({ ...p, spec_code: e.target.value }))} />
            </div>
            <div className="md:col-span-2">
              <Label>Datenblatt URL</Label>
              <Input value={form.data_sheet_url} onChange={(e) => setForm(p => ({ ...p, data_sheet_url: e.target.value }))} />
            </div>
            <div className="md:col-span-2">
              <Label>Notizen</Label>
              <Input value={form.notes} onChange={(e) => setForm(p => ({ ...p, notes: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog({ open: false, mode: 'create', item: null })}>Abbrechen</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-orange-500 hover:bg-orange-600" data-testid="master-form-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, item: null })}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{t('admin.md.deleteMasterQ')}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {deleteDialog.item?.supplier_name} - {deleteDialog.item?.supplier_article_number} wirklich löschen?
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, item: null })}>Abbrechen</Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="master-delete-confirm">Löschen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
